/**
 * Project Template System
 * Supports multiple project types with same infrastructure
 */

export type ProjectTemplateType = 
  | 'software-development'
  | 'spending-tracking'
  | 'construction'
  | 'marketing-campaign'
  | 'event-planning'
  | 'research'
  | 'custom';

export type TaskTemplateCategory = 
  // Software Development
  | 'feature' | 'bug' | 'story' | 'epic' | 'spike' | 'technical-debt'
  // Spending Tracking
  | 'expense' | 'purchase' | 'subscription' | 'travel' | 'meeting' | 'supplies'
  // Construction
  | 'feasibility' | 'engineering' | 'construction' | 'inspection' | 'selling'
  // Marketing
  | 'creative' | 'campaign' | 'content' | 'analysis'
  // Event
  | 'planning' | 'logistics' | 'vendor' | 'promotion'
  // Research
  | 'literature-review' | 'experiment' | 'analysis' | 'documentation'
  // General
  | 'task' | 'milestone' | 'deliverable';

export interface ProjectTemplate {
  id: string;
  type: ProjectTemplateType;
  name: string;
  description: string;
  icon: string;
  color: string;
  
  // Fields configuration
  fields: {
    // Which fields are visible/required
    title: { visible: boolean; required: boolean };
    description: { visible: boolean; required: boolean };
    status: { visible: boolean; required: boolean; options?: string[] };
    priority: { visible: boolean; required: boolean; options?: string[] };
    assignees: { visible: boolean; required: boolean };
    estimatedHours: { visible: boolean; required: boolean; label?: string };
    cost: { visible: boolean; required: boolean; label?: string };
    startDate: { visible: boolean; required: boolean };
    endDate: { visible: boolean; required: boolean };
    category: { visible: boolean; required: boolean; options?: TaskTemplateCategory[] };
    tags: { visible: boolean; required: boolean };
    dependencies: { visible: boolean; required: boolean };
    
    // Custom fields
    customFields?: CustomFieldDefinition[];
  };
  
  // Default task categories for this template
  taskCategories: TaskTemplateCategory[];
  
  // Default statuses
  defaultStatuses: string[];
  
  // Default priorities
  defaultPriorities: string[];
  
  // Workflow stages (for multi-phase projects)
  workflowStages?: WorkflowStage[];
  
  // Budget tracking
  budgetTracking: {
    enabled: boolean;
    categories?: BudgetCategory[];
  };
  
  // Pre-configured task templates
  taskTemplates?: TaskTemplate[];
}

export interface CustomFieldDefinition {
  id: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'checkbox' | 'currency';
  options?: string[];
  required: boolean;
  description?: string;
}

export interface WorkflowStage {
  id: string;
  name: string;
  description: string;
  order: number;
  color: string;
  taskCategories: TaskTemplateCategory[];
}

export interface BudgetCategory {
  id: string;
  name: string;
  allocatedAmount?: number;
  description?: string;
}

export interface TaskTemplate {
  id: string;
  name: string;
  description: string;
  category: TaskTemplateCategory;
  estimatedHours?: number;
  priority?: string;
  dependencies?: string[];
}

// Pre-defined templates
export const PROJECT_TEMPLATES: Record<ProjectTemplateType, ProjectTemplate> = {
  'software-development': {
    id: 'software-development',
    type: 'software-development',
    name: 'Software Development',
    description: 'Track features, bugs, sprints, and releases',
    icon: '💻',
    color: 'blue',
    fields: {
      title: { visible: true, required: true },
      description: { visible: true, required: true },
      status: { visible: true, required: true, options: ['Backlog', 'To Do', 'In Progress', 'In Review', 'Testing', 'Done'] },
      priority: { visible: true, required: true, options: ['P0', 'P1', 'P2', 'Low', 'Medium', 'High'] },
      assignees: { visible: true, required: true },
      estimatedHours: { visible: true, required: true, label: 'Story Points / Hours' },
      cost: { visible: false, required: false },
      startDate: { visible: true, required: false },
      endDate: { visible: true, required: false },
      category: { visible: true, required: true, options: ['feature', 'bug', 'story', 'epic', 'spike', 'technical-debt'] },
      tags: { visible: true, required: false },
      dependencies: { visible: true, required: false },
      customFields: [
        { id: 'sprint', name: 'Sprint', type: 'text', required: false },
        { id: 'release', name: 'Release Version', type: 'text', required: false },
        { id: 'storyPoints', name: 'Story Points', type: 'number', required: false },
        { id: 'testCoverage', name: 'Test Coverage %', type: 'number', required: false },
      ],
    },
    taskCategories: ['feature', 'bug', 'story', 'epic', 'spike', 'technical-debt'],
    defaultStatuses: ['Backlog', 'To Do', 'In Progress', 'In Review', 'Testing', 'Done'],
    defaultPriorities: ['P0', 'P1', 'P2', 'Low', 'Medium', 'High'],
    workflowStages: [
      { id: 'planning', name: 'Planning', description: 'Requirements and design', order: 1, color: 'purple', taskCategories: ['epic', 'story'] },
      { id: 'development', name: 'Development', description: 'Active development', order: 2, color: 'blue', taskCategories: ['feature', 'spike'] },
      { id: 'testing', name: 'Testing', description: 'QA and testing', order: 3, color: 'yellow', taskCategories: ['bug'] },
      { id: 'release', name: 'Release', description: 'Deployment and release', order: 4, color: 'green', taskCategories: ['deliverable'] },
    ],
    budgetTracking: {
      enabled: true,
      categories: [
        { id: 'development', name: 'Development' },
        { id: 'qa', name: 'QA & Testing' },
        { id: 'infrastructure', name: 'Infrastructure' },
        { id: 'licenses', name: 'Licenses & Tools' },
      ],
    },
    taskTemplates: [
      { id: 'user-story', name: 'User Story', description: 'As a [user], I want [goal] so that [benefit]', category: 'story', estimatedHours: 8, priority: 'Medium' },
      { id: 'bug-fix', name: 'Bug Fix', description: 'Fix issue: [description]', category: 'bug', estimatedHours: 4, priority: 'High' },
      { id: 'feature', name: 'Feature Implementation', description: 'Implement [feature name]', category: 'feature', estimatedHours: 40, priority: 'Medium' },
    ],
  },

  'spending-tracking': {
    id: 'spending-tracking',
    type: 'spending-tracking',
    name: 'Spending Tracker',
    description: 'Track expenses, purchases, and quarterly budgets',
    icon: '💰',
    color: 'green',
    fields: {
      title: { visible: true, required: true },
      description: { visible: true, required: true },
      status: { visible: true, required: true, options: ['Planned', 'Approved', 'Purchased', 'Paid', 'Reconciled'] },
      priority: { visible: true, required: false, options: ['Low', 'Medium', 'High', 'Urgent'] },
      assignees: { visible: true, required: true },
      estimatedHours: { visible: false, required: false },
      cost: { visible: true, required: true, label: 'Amount' },
      startDate: { visible: true, required: true },
      endDate: { visible: true, required: false },
      category: { visible: true, required: true, options: ['expense', 'purchase', 'subscription', 'travel', 'meeting', 'supplies'] },
      tags: { visible: true, required: false },
      dependencies: { visible: false, required: false },
      customFields: [
        { id: 'vendor', name: 'Vendor', type: 'text', required: false },
        { id: 'invoiceNumber', name: 'Invoice #', type: 'text', required: false },
        { id: 'paymentMethod', name: 'Payment Method', type: 'select', options: ['Credit Card', 'Bank Transfer', 'Cash', 'Check'], required: false },
        { id: 'recurring', name: 'Recurring', type: 'checkbox', required: false },
        { id: 'frequency', name: 'Frequency', type: 'select', options: ['Monthly', 'Quarterly', 'Annually'], required: false },
      ],
    },
    taskCategories: ['expense', 'purchase', 'subscription', 'travel', 'meeting', 'supplies'],
    defaultStatuses: ['Planned', 'Approved', 'Purchased', 'Paid', 'Reconciled'],
    defaultPriorities: ['Low', 'Medium', 'High', 'Urgent'],
    workflowStages: [
      { id: 'planning', name: 'Planning', description: 'Budget planning', order: 1, color: 'blue', taskCategories: ['expense'] },
      { id: 'approval', name: 'Approval', description: 'Awaiting approval', order: 2, color: 'yellow', taskCategories: ['expense', 'purchase'] },
      { id: 'execution', name: 'Execution', description: 'Purchase/payment', order: 3, color: 'green', taskCategories: ['purchase', 'travel'] },
      { id: 'reconciliation', name: 'Reconciliation', description: 'Accounting reconciliation', order: 4, color: 'purple', taskCategories: ['expense'] },
    ],
    budgetTracking: {
      enabled: true,
      categories: [
        { id: 'office-supplies', name: 'Office Supplies', allocatedAmount: 5000 },
        { id: 'travel', name: 'Travel & Accommodation', allocatedAmount: 20000 },
        { id: 'food', name: 'Food & Catering', allocatedAmount: 3000 },
        { id: 'meetings', name: 'Meetings & Events', allocatedAmount: 10000 },
        { id: 'subscriptions', name: 'Software Subscriptions', allocatedAmount: 15000 },
        { id: 'miscellaneous', name: 'Miscellaneous', allocatedAmount: 5000 },
      ],
    },
    taskTemplates: [
      { id: 'office-supplies', name: 'Office Supplies Purchase', description: 'Purchase office supplies', category: 'supplies', priority: 'Low' },
      { id: 'travel-booking', name: 'Travel Booking', description: 'Book travel and accommodation', category: 'travel', priority: 'Medium' },
      { id: 'subscription', name: 'Software Subscription', description: 'Renew software subscription', category: 'subscription', priority: 'Medium' },
    ],
  },

  'construction': {
    id: 'construction',
    type: 'construction',
    name: 'Construction Project',
    description: 'Track construction from feasibility to completion',
    icon: '🏗️',
    color: 'orange',
    fields: {
      title: { visible: true, required: true },
      description: { visible: true, required: true },
      status: { visible: true, required: true, options: ['Not Started', 'In Progress', 'On Hold', 'Completed', 'Inspected'] },
      priority: { visible: true, required: true, options: ['Critical', 'High', 'Medium', 'Low'] },
      assignees: { visible: true, required: true },
      estimatedHours: { visible: true, required: true, label: 'Duration (hours)' },
      cost: { visible: true, required: true, label: 'Budget' },
      startDate: { visible: true, required: true },
      endDate: { visible: true, required: true },
      category: { visible: true, required: true, options: ['feasibility', 'engineering', 'construction', 'inspection', 'selling'] },
      tags: { visible: true, required: false },
      dependencies: { visible: true, required: true },
      customFields: [
        { id: 'contractor', name: 'Contractor', type: 'text', required: false },
        { id: 'permitNumber', name: 'Permit #', type: 'text', required: false },
        { id: 'inspectionDate', name: 'Inspection Date', type: 'date', required: false },
        { id: 'materialCost', name: 'Material Cost', type: 'currency', required: false },
        { id: 'laborCost', name: 'Labor Cost', type: 'currency', required: false },
        { id: 'location', name: 'Location', type: 'text', required: false },
      ],
    },
    taskCategories: ['feasibility', 'engineering', 'construction', 'inspection', 'selling'],
    defaultStatuses: ['Not Started', 'In Progress', 'On Hold', 'Completed', 'Inspected'],
    defaultPriorities: ['Critical', 'High', 'Medium', 'Low'],
    workflowStages: [
      { id: 'feasibility', name: 'Feasibility Study', description: 'Site analysis and viability', order: 1, color: 'purple', taskCategories: ['feasibility'] },
      { id: 'design', name: 'Engineering & Design', description: 'Architectural and engineering design', order: 2, color: 'blue', taskCategories: ['engineering'] },
      { id: 'construction', name: 'Construction', description: 'Physical construction work', order: 3, color: 'orange', taskCategories: ['construction'] },
      { id: 'inspection', name: 'Inspection & QA', description: 'Quality assurance and inspections', order: 4, color: 'yellow', taskCategories: ['inspection'] },
      { id: 'handover', name: 'Sale & Handover', description: 'Marketing and sale', order: 5, color: 'green', taskCategories: ['selling'] },
    ],
    budgetTracking: {
      enabled: true,
      categories: [
        { id: 'land', name: 'Land Acquisition' },
        { id: 'design', name: 'Design & Engineering' },
        { id: 'permits', name: 'Permits & Fees' },
        { id: 'materials', name: 'Materials' },
        { id: 'labor', name: 'Labor' },
        { id: 'equipment', name: 'Equipment Rental' },
        { id: 'marketing', name: 'Marketing & Sales' },
      ],
    },
    taskTemplates: [
      { id: 'site-survey', name: 'Site Survey', description: 'Conduct site survey and analysis', category: 'feasibility', estimatedHours: 40, priority: 'Critical' },
      { id: 'foundation', name: 'Foundation Work', description: 'Foundation excavation and pouring', category: 'construction', estimatedHours: 200, priority: 'Critical', dependencies: ['site-survey'] },
      { id: 'framing', name: 'Framing', description: 'Building framing and structure', category: 'construction', estimatedHours: 400, priority: 'Critical', dependencies: ['foundation'] },
      { id: 'inspection', name: 'Building Inspection', description: 'Official building inspection', category: 'inspection', estimatedHours: 8, priority: 'Critical' },
    ],
  },

  'marketing-campaign': {
    id: 'marketing-campaign',
    type: 'marketing-campaign',
    name: 'Marketing Campaign',
    description: 'Plan and execute marketing campaigns',
    icon: '📢',
    color: 'pink',
    fields: {
      title: { visible: true, required: true },
      description: { visible: true, required: true },
      status: { visible: true, required: true, options: ['Planning', 'In Progress', 'Review', 'Live', 'Completed'] },
      priority: { visible: true, required: true, options: ['High', 'Medium', 'Low'] },
      assignees: { visible: true, required: true },
      estimatedHours: { visible: true, required: true },
      cost: { visible: true, required: true, label: 'Campaign Budget' },
      startDate: { visible: true, required: true },
      endDate: { visible: true, required: true },
      category: { visible: true, required: true, options: ['creative', 'campaign', 'content', 'analysis'] },
      tags: { visible: true, required: true },
      dependencies: { visible: true, required: false },
      customFields: [
        { id: 'channel', name: 'Channel', type: 'multiselect', options: ['Social Media', 'Email', 'Web', 'Print', 'TV', 'Radio'], required: false },
        { id: 'targetAudience', name: 'Target Audience', type: 'text', required: false },
        { id: 'kpi', name: 'KPI Target', type: 'text', required: false },
        { id: 'actualReach', name: 'Actual Reach', type: 'number', required: false },
      ],
    },
    taskCategories: ['creative', 'campaign', 'content', 'analysis'],
    defaultStatuses: ['Planning', 'In Progress', 'Review', 'Live', 'Completed'],
    defaultPriorities: ['High', 'Medium', 'Low'],
    budgetTracking: { enabled: true },
  },

  'event-planning': {
    id: 'event-planning',
    type: 'event-planning',
    name: 'Event Planning',
    description: 'Organize conferences, meetings, and events',
    icon: '🎉',
    color: 'purple',
    fields: {
      title: { visible: true, required: true },
      description: { visible: true, required: true },
      status: { visible: true, required: true, options: ['Planning', 'Booked', 'Confirmed', 'In Progress', 'Completed'] },
      priority: { visible: true, required: true, options: ['High', 'Medium', 'Low'] },
      assignees: { visible: true, required: true },
      estimatedHours: { visible: true, required: false },
      cost: { visible: true, required: true, label: 'Cost' },
      startDate: { visible: true, required: true },
      endDate: { visible: true, required: true },
      category: { visible: true, required: true, options: ['planning', 'logistics', 'vendor', 'promotion'] },
      tags: { visible: true, required: false },
      dependencies: { visible: true, required: false },
      customFields: [
        { id: 'venue', name: 'Venue', type: 'text', required: false },
        { id: 'attendees', name: 'Expected Attendees', type: 'number', required: false },
        { id: 'catering', name: 'Catering Required', type: 'checkbox', required: false },
      ],
    },
    taskCategories: ['planning', 'logistics', 'vendor', 'promotion'],
    defaultStatuses: ['Planning', 'Booked', 'Confirmed', 'In Progress', 'Completed'],
    defaultPriorities: ['High', 'Medium', 'Low'],
    budgetTracking: { enabled: true },
  },

  'research': {
    id: 'research',
    type: 'research',
    name: 'Research Project',
    description: 'Academic or market research projects',
    icon: '🔬',
    color: 'teal',
    fields: {
      title: { visible: true, required: true },
      description: { visible: true, required: true },
      status: { visible: true, required: true, options: ['Planning', 'Data Collection', 'Analysis', 'Writing', 'Review', 'Published'] },
      priority: { visible: true, required: true, options: ['High', 'Medium', 'Low'] },
      assignees: { visible: true, required: true },
      estimatedHours: { visible: true, required: true },
      cost: { visible: true, required: false },
      startDate: { visible: true, required: true },
      endDate: { visible: true, required: true },
      category: { visible: true, required: true, options: ['literature-review', 'experiment', 'analysis', 'documentation'] },
      tags: { visible: true, required: true },
      dependencies: { visible: true, required: false },
      customFields: [
        { id: 'methodology', name: 'Methodology', type: 'text', required: false },
        { id: 'dataSource', name: 'Data Source', type: 'text', required: false },
        { id: 'findings', name: 'Key Findings', type: 'text', required: false },
      ],
    },
    taskCategories: ['literature-review', 'experiment', 'analysis', 'documentation'],
    defaultStatuses: ['Planning', 'Data Collection', 'Analysis', 'Writing', 'Review', 'Published'],
    defaultPriorities: ['High', 'Medium', 'Low'],
    budgetTracking: { enabled: true },
  },

  'custom': {
    id: 'custom',
    type: 'custom',
    name: 'Custom Project',
    description: 'Create a custom project with your own configuration',
    icon: '⚙️',
    color: 'gray',
    fields: {
      title: { visible: true, required: true },
      description: { visible: true, required: true },
      status: { visible: true, required: true },
      priority: { visible: true, required: true },
      assignees: { visible: true, required: true },
      estimatedHours: { visible: true, required: false },
      cost: { visible: true, required: false },
      startDate: { visible: true, required: false },
      endDate: { visible: true, required: false },
      category: { visible: true, required: false },
      tags: { visible: true, required: false },
      dependencies: { visible: true, required: false },
    },
    taskCategories: ['task', 'milestone', 'deliverable'],
    defaultStatuses: ['To Do', 'In Progress', 'Done'],
    defaultPriorities: ['High', 'Medium', 'Low'],
    budgetTracking: { enabled: false },
  },
};
